import type { FC } from 'react';
import React, { useCallback, Fragment, useState, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { ApolloQueryResult } from 'apollo-client';
import { NetworkStatus } from '@confluence/apollo-relay-interop/entry-points/NetworkStatus.ts';
import { css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { token } from '@atlaskit/tokens';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import AkSpinner from '@atlaskit/spinner/spinner';
import Button from '@atlaskit/button/custom-theme-button';
import FeedbackIcon from '@atlaskit/icon/core/feedback';

import { expVal, expValEquals } from '@confluence/feature-experiments';
import { type UseQueryInteropSupportedResult } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { FeedType } from '../useFeedTypeSelector.ts';
import { type FollowingFeedNode, isVisibleFollowingFeedNode } from '../feedVisibility.ts';
import { FeedOnboardingMessage } from '../Changeboarding/index.ts';
import { FeedRecommendations, useFeedRecommendations } from '../FeedRecommendations/index.ts';
import { FETCH_MORE_PAGE_SIZE } from '../feedConstants.ts';
import type { FeedContentQueryData as FeedContentQueryType } from '../feedCardTypes.ts';
import type { FollowingFeedSortByValue } from '../Feed.tsx';

import { useFeedShouldHideComments } from './useFeedShouldHideComments.ts';
import { FeedCard } from './FeedCard/index.ts';
import { FeedCardsWrapper } from './FeedCardsWrapper.tsx';
import { ExitFeedAllyButton } from './ExitFeedAllyButton.tsx';

type Props = {
	feedContentQuery: UseQueryInteropSupportedResult<FeedContentQueryType, any>;
	openActivityFilterModal: () => void;
	handleFocusRef: () => void;
	followingFeedSortBy?: FollowingFeedSortByValue;
	hasFollowedAnyone: boolean;
};

export const i18n: {
	allActivity: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	manageFeed: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	allActivity: {
		id: 'feed.fallback.message',
		defaultMessage: 'To see personalized activity edit your feed by following people and spaces.',
		description: 'Tells the user that we are falling back to all activity on empty feed',
	},
	manageFeed: {
		id: 'feed.fallback.manage.following',
		defaultMessage: 'Edit feed',
		description: 'Button text that opens a modal that allows the user to configure settings',
	},
});

const feedRecommendationsContainerStyle = css({
	background: token('color.background.input'),
	position: 'relative',
	padding: `${token('space.100')} ${token('space.200')}`,
	paddingBottom: token('space.200'),
	marginBottom: token('space.200'),
	overflow: 'hidden',
	height: '194px',
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: token('radius.large'),
	'&:hover': {
		boxShadow: token('elevation.shadow.raised'),
	},
});

const fallbackToAllActivityBannerWrapperStyle = css({
	display: 'flex',
	alignItems: 'center',
	textAlign: 'left',
	background:
		'linear-gradient(208.78deg,rgba(255, 240, 179, 0.41) 1.19%,rgba(179, 245, 255, 0.41) 98.53%)', // eslint-disable-line @atlaskit/design-system/ensure-design-token-usage
	padding: token('space.150'),
	borderRadius: token('radius.large'),
	marginBottom: token('space.200'),
});

const spinnerWrapperStyle = css({
	height: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

export const ActivityFeedCards: FC<Props> = ({
	feedContentQuery,
	openActivityFilterModal,
	handleFocusRef,
	followingFeedSortBy,
	hasFollowedAnyone,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data, loading, error, networkStatus, fetchMore } = feedContentQuery;
	const [consecutiveOnboardingPulls, setConsecutiveOnboardingPulls] = useState(0);
	const MAX_ONBOARDING_PULLS = 3;

	const shouldIncludeItem = useCallback(
		(item: FollowingFeedNode | null) => isVisibleFollowingFeedNode(item),
		[],
	);
	const hasNextPage =
		data?.feed?.pageInfo?.hasNextPage &&
		(expValEquals('cc-activation-use-generated-by-exp-gate', 'cohort', 'test')
			? consecutiveOnboardingPulls < MAX_ONBOARDING_PULLS
			: true);
	const endCursor = data?.feed?.pageInfo?.endCursor;
	const allResults = data?.feed?.nodes || [];
	const results = allResults.filter(shouldIncludeItem);
	const servingRecommendations = !!data?.getFeedUserConfig?.servingRecommendations;

	const isFollowingEmptyStateBanner =
		Boolean(data) &&
		results.length === 0 &&
		expVal('cc_mohiti_for_you_empty_states_exp', 'isEnabled', false);

	const { showFeedRecs, setShowFeedRecs, recsLoading, recs, setRecs, feedRecError } =
		useFeedRecommendations();

	const shouldHideComments = useFeedShouldHideComments();

	useEffect(() => {
		if (
			data &&
			allResults.length > 0 &&
			results.length === 0 &&
			expValEquals('cc-activation-use-generated-by-exp-gate', 'cohort', 'test')
		) {
			setConsecutiveOnboardingPulls(1);
		}
	}, [data, allResults.length, results.length]);

	const linkButtonTheme = (currentTheme, themeProps) => {
		const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
		return {
			buttonStyles: {
				...buttonStyles,
				fontWeight: token('font.weight.regular'),
			},
			spinnerStyles,
		};
	};

	const injectFeedSortByIntoCursor = (cursor: string, feedSortBy?: FollowingFeedSortByValue) => {
		if (!feedSortBy) {
			return cursor;
		}

		const decodedCursor = atob(cursor);
		const parsedCursor = JSON.parse(decodedCursor);

		if (parsedCursor.feedSortOrder === feedSortBy.toUpperCase()) {
			return cursor;
		}

		return btoa(JSON.stringify({ ...parsedCursor, feedSortOrder: feedSortBy.toUpperCase() }));
	};

	const loadNextPage = useCallback(async (): Promise<
		ApolloQueryResult<FeedContentQueryType> | undefined
	> => {
		try {
			if (!endCursor) {
				return undefined;
			}

			const newResult = await fetchMore({
				variables: {
					first: FETCH_MORE_PAGE_SIZE,
					after: endCursor,
					sortBy: followingFeedSortBy,
				},
				updateQuery: (prevResults: FeedContentQueryType, { fetchMoreResult }) => {
					if (!fetchMoreResult) {
						return prevResults;
					}

					// Deep clone required — a shallow spread shares node references with Apollo's
					// InMemoryCache and corrupts the cache write on fetchMore.
					const fetchMoreResultCopy = JSON.parse(JSON.stringify(fetchMoreResult));

					const newNodes = fetchMoreResultCopy.feed?.nodes || [];
					const filteredNewNodes = newNodes.filter(shouldIncludeItem);

					if (expValEquals('cc-activation-use-generated-by-exp-gate', 'cohort', 'test')) {
						if (filteredNewNodes.length === 0 && newNodes.length > 0) {
							setConsecutiveOnboardingPulls((prev) => prev + 1);
						} else {
							setConsecutiveOnboardingPulls(0);
						}
					}

					if (fetchMoreResultCopy.feed?.nodes && prevResults.feed?.nodes) {
						fetchMoreResultCopy.feed.nodes = prevResults.feed.nodes.concat(filteredNewNodes);

						if (fetchMoreResultCopy.feed.pageInfo.endCursor) {
							fetchMoreResultCopy.feed.pageInfo.endCursor = injectFeedSortByIntoCursor(
								fetchMoreResultCopy.feed.pageInfo.endCursor,
								followingFeedSortBy,
							);
						} else {
							fetchMoreResultCopy.feed.pageInfo.endCursor =
								prevResults.feed?.pageInfo?.endCursor ?? '';
						}
					}

					return fetchMoreResultCopy;
				},
			});
			if (newResult.data) {
				const previousVisibleCount = (data?.feed.nodes || []).filter(shouldIncludeItem).length;
				const newVisibleCount = (newResult.data.feed?.nodes || []).filter(shouldIncludeItem).length;
				const visibleItemsAdded = newVisibleCount - previousVisibleCount;

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'paginated',
						actionSubject: 'feed',
						source: 'homeScreen',
						attributes: {
							resultCount: visibleItemsAdded,
						},
					},
				}).fire();
			}

			if (newResult.errors) {
				throw newResult.errors;
			}

			return newResult;
		} catch (e) {
			throw e;
		}
	}, [
		endCursor,
		fetchMore,
		followingFeedSortBy,
		createAnalyticsEvent,
		data,
		setConsecutiveOnboardingPulls,
		shouldIncludeItem,
	]);

	const disableAnimation = Boolean(
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		process.env.REACT_SSR || (window.__SSR_RENDERED__ && networkStatus !== NetworkStatus.fetchMore),
	);

	const handleManageButtonClick = useCallback(() => {
		openActivityFilterModal();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'item',
				actionSubjectId: 'editFeed',
				source: 'homeScreen',
			},
		}).fire();
	}, [createAnalyticsEvent, openActivityFilterModal]);

	const shouldFallbackBasedOnCursor = (cursor) => {
		if (!cursor) return false;
		try {
			const decodedCursor = atob(cursor);
			const parsedCursor = JSON.parse(decodedCursor);
			return results.length > 0 ? (parsedCursor.fallbackToAllActivity ?? false) : false;
		} catch {
			return false;
		}
	};

	return (
		<Fragment>
			{!isFollowingEmptyStateBanner && (
				<FeedOnboardingMessage
					openActivityFilterModal={openActivityFilterModal}
					servingRecommendations={servingRecommendations}
				/>
			)}
			<FeedCardsWrapper
				loading={loading}
				firstPageError={error}
				networkStatus={networkStatus}
				endCursor={endCursor}
				hasNextPage={hasNextPage}
				loadNextPage={loadNextPage}
				openActivityFilterModal={openActivityFilterModal}
				feedType={FeedType.FOLLOWING}
				hasFollowedAnyone={hasFollowedAnyone}
				hasResults={results.length > 0}
			>
				{shouldFallbackBasedOnCursor(endCursor) && (
					<div
						css={fallbackToAllActivityBannerWrapperStyle}
						data-testid="fallback-to-all-activity-banner"
					>
						<Inline space="space.100">
							<FeedbackIcon label="" spacing="spacious" />
							<span>
								<Text>{intl.formatMessage(i18n.allActivity)}</Text>
							</span>
							<Button
								onClick={handleManageButtonClick}
								theme={linkButtonTheme}
								appearance="link"
								spacing="none"
							>
								<Text>{intl.formatMessage(i18n.manageFeed)}</Text>
							</Button>
						</Inline>
					</div>
				)}
				{results.map((result, index) => {
					return (
						<Fragment key={result.content?.id}>
							{result && (
								<FeedCard
									feedItem={result}
									ranking={index}
									disableAnimation={disableAnimation}
									shouldHideComments={shouldHideComments}
									followingFeedSortBy={followingFeedSortBy}
								/>
							)}
							<ExitFeedAllyButton
								onExit={handleFocusRef}
								source="following-feed"
								feedCardIndex={index}
							/>
							{index === 2 && showFeedRecs && (
								<>
									<div css={feedRecommendationsContainerStyle}>
										{recsLoading ? (
											<div css={spinnerWrapperStyle}>
												<AkSpinner size="large" />
											</div>
										) : (
											<FeedRecommendations
												position={index + 1}
												setShowFeedRecs={setShowFeedRecs}
												recs={recs}
												setRecs={setRecs}
												feedRecError={feedRecError}
											/>
										)}
									</div>
									<ExitFeedAllyButton
										onExit={handleFocusRef}
										source="following-feed"
										isAfterRecommendations
									/>
								</>
							)}
						</Fragment>
					);
				})}
			</FeedCardsWrapper>
		</Fragment>
	);
};
